import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { ChevronDown, Trash2, Mail, Phone } from "lucide-react";

const STATUSES = ["new", "read", "responded"];
const badgeCls = (s) =>
  s === "new"
    ? "border-[#D4AF37]/50 text-[#D4AF37]"
    : s === "read"
    ? "border-[#A3A3A3] text-[#A3A3A3]"
    : "border-[#32D74B]/50 text-[#32D74B]";

export default function AdminInquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = () => {
    setLoading(true);
    api
      .get("/inquiries")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/inquiries/${id}`, { status });
      setItems((arr) =>
        arr.map((i) => (i.id === id ? { ...i, status } : i)),
      );
      toast.success("Status updated.");
    } catch {
      toast.error("Update failed.");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/inquiries/${id}`);
      setItems((arr) => arr.filter((i) => i.id !== id));
      toast.success("Inquiry deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="admin-inquiries-page">
      <p className="overline mb-2">Inbox</p>
      <h1 className="font-serif-luxe text-5xl leading-none">Inquiries</h1>
      <p className="mt-4 text-[#A3A3A3]">
        Every guest inquiry that arrives through the site.
      </p>

      {loading ? (
        <p className="mt-10 text-[#A3A3A3]">Loading…</p>
      ) : items.length === 0 ? (
        <p
          className="mt-10 text-[#A3A3A3]"
          data-testid="admin-inquiries-empty"
        >
          No inquiries yet.
        </p>
      ) : (
        <div className="mt-10 space-y-3">
          {items.map((i) => {
            const isOpen = expanded[i.id];
            return (
              <div
                key={i.id}
                data-testid={`inquiry-${i.id}`}
                className="border border-[#F5F5DC]/10 bg-[#1C1C1E]/60"
              >
                <button
                  onClick={() =>
                    setExpanded((e) => ({ ...e, [i.id]: !e[i.id] }))
                  }
                  className="w-full px-6 py-5 flex items-center gap-6 text-left hover:bg-[#1C1C1E]"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <p className="font-serif-luxe text-2xl leading-none">
                        {i.name}
                      </p>
                      <span
                        className={`text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 border ${badgeCls(
                          i.status,
                        )}`}
                      >
                        {i.status}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-[#737373] truncate">
                      {i.property_name || "General inquiry"} ·{" "}
                      {format(new Date(i.created_at), "MMM d, yyyy p")}
                    </p>
                  </div>
                  <ChevronDown
                    size={16}
                    className={`text-[#A3A3A3] transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="border-t border-[#F5F5DC]/10 px-6 py-6 space-y-5">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <KV label="Email" value={
                        <a href={`mailto:${i.email}`} className="text-[#D4AF37] hover:underline flex items-center gap-1">
                          <Mail size={12}/> {i.email}
                        </a>
                      } />
                      <KV label="Phone" value={
                        i.phone ? (
                          <a href={`tel:${i.phone}`} className="text-[#D4AF37] hover:underline flex items-center gap-1">
                            <Phone size={12}/> {i.phone}
                          </a>
                        ) : "—"
                      } />
                      <KV label="Check-in" value={i.check_in || "—"} />
                      <KV label="Check-out" value={i.check_out || "—"} />
                      <KV label="Guests" value={i.guests || "—"} />
                      <KV label="Property" value={i.property_name || "General"} />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                        Message
                      </p>
                      <p className="whitespace-pre-line text-[#F5F5DC]/90 leading-relaxed">
                        {i.message}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-[#F5F5DC]/5">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            data-testid={`status-${i.id}`}
                            className="bg-transparent border-[#F5F5DC]/20 rounded-none"
                          >
                            Set status: {i.status} <ChevronDown size={14} className="ml-2"/>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-[#1C1C1E] border-[#F5F5DC]/10">
                          {STATUSES.map((s) => (
                            <DropdownMenuItem
                              key={s}
                              onClick={() => updateStatus(i.id, s)}
                              data-testid={`status-${i.id}-${s}`}
                            >
                              {s}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <a
                        href={`mailto:${i.email}?subject=${encodeURIComponent("Re: " + (i.property_name || "your inquiry"))}`}
                        className="px-4 py-2 bg-[#D4AF37] text-[#121212] text-xs tracking-[0.2em] uppercase hover:bg-[#B3922E]"
                        data-testid={`reply-${i.id}`}
                      >
                        Reply by email
                      </a>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button
                            className="ml-auto p-2 text-[#737373] hover:text-[#FF453A]"
                            data-testid={`delete-inquiry-${i.id}`}
                          >
                            <Trash2 size={16}/>
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-[#1C1C1E] border-[#F5F5DC]/10">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this inquiry?</AlertDialogTitle>
                            <AlertDialogDescription>This can't be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-transparent border-[#F5F5DC]/20">Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => remove(i.id)}
                              className="bg-[#FF453A] hover:bg-[#FF453A]/80"
                              data-testid={`confirm-delete-inquiry-${i.id}`}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#737373]">{label}</p>
      <p className="mt-1">{value}</p>
    </div>
  );
}
